/**
 * Gentle journal statistics + visual analytics + export + reminder.
 * No guilt: celebrate what was written, not punish what was not.
 *
 * Analytics (CORE PILLAR): summary cards + bar chart of entries per
 * month (inline, theme-aware, accessible) + streak + text report. Everything is
 * computed locally; no charting libraries.
 */
import { createRoute } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { BarChart3, BookOpen, CalendarDays, Flame, Type } from "@fleet/icons";
import { computeStreaks, localDateKey } from "@journal/shared";
import {
  useEntryDaysQuery,
  useJournalTotalsQuery,
  useMonthlyStatsQuery,
} from "@journal/shared/solid";
import { appRoute } from "./route";
import { Card, CardContent, PageContainer } from "@fleet/ui";
import { AppErrorBoundary } from "@/components/app-error-boundary";
import { useI18n } from "@/providers/i18n-provider";
import { ExportDialog } from "@/features/journal/components/export-dialog";
import { ReminderSettingsCard } from "@/features/journal/components/reminder-settings-card";
import { BarChart, type BarDatum } from "@/features/journal/components/bar-chart";
import { formatMonthHeading } from "@/features/journal/format";
import { useJournalData } from "@/features/journal/hooks/use-journal-data";
import { GamificationPanel } from "@/features/journal/components/gamification-panel";

const MONTHS_SHOWN = 12;
const MONTH_PREFIX_LENGTH = 7;
const CHART_MONTHS = 6;

function StatCard(props: { icon: typeof BookOpen; label: string; value: string | number }) {
  return (
    <Card>
      <CardContent class="flex items-center gap-3 p-4">
        <span class="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <props.icon class="h-4.5 w-4.5" />
        </span>
        <div class="min-w-0">
          <p class="truncate text-xs text-muted-foreground/70">{props.label}</p>
          <p class="text-lg font-bold text-foreground">{props.value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function StatsContent() {
  const { t, locale } = useI18n();
  const { db, drizzle, userId, staleTime } = useJournalData();

  const totals = useJournalTotalsQuery({ db, drizzle, userId, staleTime });
  const monthly = useMonthlyStatsQuery({
    db,
    drizzle,
    userId,
    staleTime,
    limit: MONTHS_SHOWN,
  });
  const days = useEntryDaysQuery({ db, drizzle, userId, staleTime });

  const streaks = createMemo(() => {
    const dates = (days.data ?? []).map((d) => d.entry_date).filter((d): d is string => Boolean(d));
    return computeStreaks(dates, localDateKey());
  });

  const monthLabel = (month: string) => {
    if (month.length < MONTH_PREFIX_LENGTH) {
      return month;
    }
    const [y, m] = month.split("-").map(Number);
    if (!y || !m) {
      return month;
    }
    return formatMonthHeading(y, m - 1, locale());
  };

  // Short month label (month only) for the chart's X axis.
  const monthShort = (month: string) => {
    const [y, m] = month.split("-").map(Number);
    if (!y || !m) {
      return month;
    }
    return new Intl.DateTimeFormat(locale(), { month: "short" }).format(new Date(y, m - 1, 1));
  };

  // Ascending chronological series for the chart (the last CHART_MONTHS).
  const chartData = createMemo<BarDatum[]>(() =>
    (monthly.data ?? [])
      .slice(0, CHART_MONTHS)
      .toReversed()
      .map((row) => ({
        label: monthShort(row.month),
        value: row.entries,
        title: `${monthLabel(row.month)}: ${t(msg`{count} entries`, { count: row.entries })}`,
      })),
  );

  const chartSummary = createMemo(() => {
    const rows = monthly.data ?? [];
    if (rows.length === 0) {
      return t(msg`Write your first entry to see your months here.`);
    }
    const best = rows.reduce((a, b) => (b.entries > a.entries ? b : a));
    return t(msg`Entries per month. Most active: {month} with {count} entries.`, {
      month: monthLabel(best.month),
      count: best.entries,
    });
  });

  return (
    <>
      <GamificationPanel />

      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="stats-cards">
        <StatCard icon={BookOpen} label={t(msg`Entries`)} value={totals.data?.entries ?? 0} />
        <StatCard icon={CalendarDays} label={t(msg`Days written`)} value={totals.data?.days ?? 0} />
        <StatCard icon={Type} label={t(msg`Words`)} value={totals.data?.words ?? 0} />
        <StatCard icon={Flame} label={t(msg`Longest streak`)} value={streaks().longest} />
      </div>

      {/* Highlighted current streak (quick guilt-free report). */}
      <Show when={streaks().current > 0}>
        <Card>
          <CardContent class="flex items-center gap-3 p-4">
            <span class="flex h-10 w-10 items-center justify-center rounded-full bg-orange-400/15 text-orange-400">
              <Flame class="h-5 w-5" />
            </span>
            <p class="text-sm text-foreground/90" data-testid="stats-current-streak">
              {t(msg`You're on a {count}-day writing streak. Keep it going!`, {
                count: streaks().current,
              })}
            </p>
          </CardContent>
        </Card>
      </Show>

      <ReminderSettingsCard />

      {/* Bar chart: entries per month (visual + accessible). */}
      <section class="flex flex-col gap-2">
        <h2 class="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <BarChart3 class="h-4 w-4" />
          {t(msg`Entries per month`)}
        </h2>
        <Card>
          <CardContent class="p-4" data-testid="stats-bar-chart">
            <BarChart data={chartData} summary={chartSummary()} />
          </CardContent>
        </Card>
      </section>

      <section class="flex flex-col gap-2">
        <h2 class="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <BarChart3 class="h-4 w-4" />
          {t(msg`Per month`)}
        </h2>
        <Show
          when={(monthly.data ?? []).length > 0}
          fallback={
            <p class="text-sm text-muted-foreground/70">
              {t(msg`Write your first entry to see your months here.`)}
            </p>
          }
        >
          <Card>
            <CardContent class="p-0">
              <table class="w-full text-sm" data-testid="stats-monthly">
                <thead>
                  <tr class="border-b border-border/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground/60">
                    <th class="px-4 py-2.5 font-semibold">{t(msg`Month`)}</th>
                    <th class="px-4 py-2.5 text-right font-semibold">{t(msg`Days`)}</th>
                    <th class="px-4 py-2.5 text-right font-semibold">{t(msg`Entries`)}</th>
                    <th class="px-4 py-2.5 text-right font-semibold">{t(msg`Words`)}</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={monthly.data ?? []}>
                    {(row) => (
                      <tr class="border-b border-border/20 last:border-0">
                        <td class="px-4 py-2.5 capitalize text-foreground">
                          {monthLabel(row.month)}
                        </td>
                        <td class="px-4 py-2.5 text-right text-foreground/80">{row.days}</td>
                        <td class="px-4 py-2.5 text-right text-foreground/80">{row.entries}</td>
                        <td class="px-4 py-2.5 text-right text-foreground/80">{row.words}</td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </CardContent>
          </Card>
        </Show>
      </section>
    </>
  );
}

function StatsPage() {
  const { t } = useI18n();

  return (
    <PageContainer maxWidth="2xl" class="flex flex-col gap-5 pb-16">
      <header class="flex items-center justify-between gap-2 pt-2">
        <h1 class="text-xl font-bold text-foreground">{t(msg`Statistics`)}</h1>
        <ExportDialog />
      </header>

      <AppErrorBoundary label="stats">
        <StatsContent />
      </AppErrorBoundary>
    </PageContainer>
  );
}

/** Export dialog state in the URL (G3 #15b): open + range +
 * format, so reloading or pasting `/stats?export=1&from=&to=&fmt=` reopens the
 * same dialog. */
interface StatsSearch {
  export?: boolean;
  from?: string;
  to?: string;
  fmt?: "md" | "txt";
}

export const statsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/stats",
  validateSearch: (raw: Record<string, unknown>): StatsSearch => ({
    // Include NUMBER 1: TanStack parses `?export=1` as number 1 (not
    // string), and without this case the param was discarded at boot (CC-18).
    export:
      raw.export === true || raw.export === 1 || raw.export === "1" || raw.export === "true"
        ? true
        : undefined,
    from: typeof raw.from === "string" && raw.from !== "" ? raw.from : undefined,
    to: typeof raw.to === "string" && raw.to !== "" ? raw.to : undefined,
    fmt: raw.fmt === "txt" ? "txt" : raw.fmt === "md" ? "md" : undefined,
  }),
  component: StatsPage,
});
